import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, inr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import TopBar from "../components/TopBar";
import { Card, Button, Input, Chip, Select } from "../components/ui";
import { LogOut, Copy, Users, Wallet, Save, Shield, Mail, Settings, DollarSign, ArrowUpRight } from "lucide-react";
import { Avatar } from "./MembersPage";
import { InstallAppButton } from "../components/InstallPrompt";
import ShareJoinStepsButton from "../components/ShareJoinStepsButton";

const SKILL_LEVELS = [
  { value: "good", label: "Good" },
  { value: "average", label: "Average" },
  { value: "ok", label: "OK" },
];

const UMPIRE_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

export default function ProfilePage() {
  const nav = useNavigate();
  const { user, club, logout, refreshMe } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", mobile: user?.mobile || "", upi_id: user?.upi_id || "",
  });
  const [skills, setSkills] = useState({
    batting_skill: user?.batting_skill || "ok",
    bowling_skill: user?.bowling_skill || "ok",
    fielding_skill: user?.fielding_skill || "ok",
    keeping_skill: user?.keeping_skill || "ok",
    can_umpire: user?.can_umpire ? "true" : "false",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({ name: user.name, email: user.email || "", mobile: user.mobile || "", upi_id: user.upi_id || "" });
      setSkills({
        batting_skill: user.batting_skill || "ok",
        bowling_skill: user.bowling_skill || "ok",
        fielding_skill: user.fielding_skill || "ok",
        keeping_skill: user.keeping_skill || "ok",
        can_umpire: user.can_umpire ? "true" : "false",
      });
    }
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    const emailChanged = form.email.trim().toLowerCase() !== (user?.email || "");
    if (emailChanged && !window.confirm(`Change your login email to ${form.email.trim()}? You'll use it to log in from now on.`)) return;
    setSaving(true);
    try {
      await api.patch("/auth/me", {
        ...form,
        batting_skill: skills.batting_skill,
        bowling_skill: skills.bowling_skill,
        fielding_skill: skills.fielding_skill,
        keeping_skill: skills.keeping_skill,
        can_umpire: skills.can_umpire === "true",
        skills_set: true,
      });
      await refreshMe();
      toast.success("Profile updated");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const copyJoin = () => {
    navigator.clipboard?.writeText(club?.join_code || "");
    toast.success("Join code copied");
  };

  const doLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const goToClubSettings = () => {
    if (club?.id) {
      nav(`/clubs/${club.id}/settings`);
    }
  };

  return (
    <div className="pb-28">
      <TopBar title="Profile" subtitle="You & your club" />

      <div className="px-5 space-y-4">
        <Card className="flex items-center gap-4">
          <Avatar member={{ name: user?.name, photo: user?.photo }} size={64} />
          <div>
            <div className="font-bold text-lg">{user?.name}</div>
            <div className="text-sm text-ink-muted truncate">{user?.email}</div>
            <div className="flex gap-2 mt-1.5">
              <Chip tone="dark">{club?.my_role || "member"}</Chip>
              {user?.role === "admin" && <Chip tone="warning">admin</Chip>}
            </div>
          </div>
        </Card>

        {club && (
          <Card>
            <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-2 flex items-center gap-1">
              <Users size={12} /> Your Club
            </div>
            <div className="font-bold text-lg">{club.name}</div>
            {club.upi_id && <div className="text-sm text-ink-muted">UPI: {club.upi_id}</div>}
            <div className="mt-3 flex items-center gap-2">
              <div className="flex-1 h-11 px-3 rounded-2xl bg-black/[0.04] font-mono font-bold text-center tracking-widest">
                {club.join_code}
              </div>
              <Button variant="outline" size="md" onClick={copyJoin} data-testid="profile-copy-code">
                <Copy size={16} /> Copy
              </Button>
            </div>
            <p className="text-xs text-ink-muted mt-2">
              Share this code with your teammates to invite them.
            </p>
            {["owner", "admin", "manager"].includes(club.my_role) && (
              <ShareJoinStepsButton club={club} className="mt-3" />
            )}
          </Card>
        )}

        <Card>
          <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-3">
            Cricket Skills
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Batting"
              value={skills.batting_skill}
              onChange={(e) => setSkills({ ...skills, batting_skill: e.target.value })}
              data-testid="profile-batting"
            >
              {SKILL_LEVELS.map((level) => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </Select>
            <Select
              label="Bowling"
              value={skills.bowling_skill}
              onChange={(e) => setSkills({ ...skills, bowling_skill: e.target.value })}
              data-testid="profile-bowling"
            >
              {SKILL_LEVELS.map((level) => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </Select>
            <Select
              label="Fielding"
              value={skills.fielding_skill}
              onChange={(e) => setSkills({ ...skills, fielding_skill: e.target.value })}
              data-testid="profile-fielding"
            >
              {SKILL_LEVELS.map((level) => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </Select>
            <Select
              label="Keeping"
              value={skills.keeping_skill}
              onChange={(e) => setSkills({ ...skills, keeping_skill: e.target.value })}
              data-testid="profile-keeping"
            >
              {SKILL_LEVELS.map((level) => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="mt-4">
            <Select
              label="Can Umpire?"
              value={skills.can_umpire}
              onChange={(e) => setSkills({ ...skills, can_umpire: e.target.value })}
              data-testid="profile-umpire"
            >
              {UMPIRE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
        </Card>

        <Card>
          <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-3">
            Edit profile
          </div>
          <form onSubmit={save} className="space-y-3">
            <Input label="Name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="profile-name" />
            <Input label="Email (login)" type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} required data-testid="profile-email" />
            <Input label="Mobile" value={form.mobile} onChange={(e)=>setForm({...form, mobile:e.target.value})} data-testid="profile-mobile" />
            <Input label="UPI ID" placeholder="you@upi" value={form.upi_id} onChange={(e)=>setForm({...form, upi_id:e.target.value})} data-testid="profile-upi" />
            <Button type="submit" size="lg" className="w-full" disabled={saving} data-testid="profile-save-btn">
              <Save size={18} /> {saving ? "Saving…" : "Save changes"}
            </Button>
          </form>
        </Card>

        {club && club.my_role === "owner" && (
          <Button variant="outline" size="md" className="w-full" onClick={goToClubSettings} data-testid="club-settings-btn">
            <Settings size={16} /> Club Settings
          </Button>
        )}

        <Button variant="outline" size="md" className="w-full" onClick={() => nav("/invitations")} data-testid="invitations-btn">
          <Mail size={16} /> Club Invitations
        </Button>

        <Button variant="outline" size="md" className="w-full" onClick={() => nav("/pricing")} data-testid="pricing-btn">
          <DollarSign size={16} /> View Pricing
        </Button>

        <InstallAppButton className="w-full" />

        <Button variant="outline" size="lg" className="w-full text-red-500 border-red-200" onClick={doLogout} data-testid="logout-btn">
          <LogOut size={16}/> Sign out
        </Button>
      </div>
    </div>
  );
}
